/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useCallback, useState } from 'react';
import { LocalAudioTrack } from 'twilio-video';

import useIsTrackEnabled from './useIsTrackEnabled';
import useVideoContext from './useVideoContext';

export default function useLocalAudioToggle() {
  const { room, localTracks, getLocalAudioTrack, removeLocalAudioTrack, onError } = useVideoContext();
  const localParticipant = room?.localParticipant;
  const audioTrack = localTracks.find((track) => track.kind === 'audio') as LocalAudioTrack;
  const isEnabled = useIsTrackEnabled(audioTrack);
  const [isPublishing, setIsPublishing] = useState(false);

  const toggleAudioEnabled = useCallback(async () => {
    if (isPublishing) return;

    // A track whose MediaStreamTrack has ended cannot be revived by
    // enable(): the flag flips and the button lights up while nothing is
    // transmitted. Treat that as no track at all so it gets replaced.
    const isLive = audioTrack?.mediaStreamTrack?.readyState === 'live';

    if (audioTrack && isLive) {
      if (audioTrack.isEnabled) {
        audioTrack.disable();
      } else {
        audioTrack.enable();
      }
      return;
    }

    // No usable microphone. This branch previously did nothing at all -
    // the hook just returned when audioTrack was undefined - so a user
    // whose mic failed to acquire at join time was left with a button
    // that never responded for the rest of the call. Acquire and publish
    // instead, mirroring how useLocalVideoToggle republishes its track.
    // Asks for the default device rather than the stored deviceId, since
    // a stale stored device is one of the ways acquisition fails in the
    // first place.
    setIsPublishing(true);
    if (audioTrack) {
      const localTrackPublication = localParticipant?.unpublishTrack(audioTrack);
      localParticipant?.emit('trackUnpublished', localTrackPublication);
      removeLocalAudioTrack();
    }
    await getLocalAudioTrack()
      .then((track: LocalAudioTrack) => {
        localParticipant?.publishTrack(track);
      })
      .catch(onError)
      .finally(() => {
        setIsPublishing(false);
      });
  }, [isPublishing, audioTrack, localParticipant, removeLocalAudioTrack, getLocalAudioTrack, onError]);

  return [isEnabled, toggleAudioEnabled] as const;
}
